import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnDestroy
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { isNil, get } from 'lodash';
import {
  REMOVED,
  IValueChangedEvent,
  IComponentStateHistory,
  BaseSetupTab,
  outputParams
} from '@nrwl/test-setup';

import {
  EntityCacheProvider,
  SchedulerFormat,
  TransformationService,
  Debounce,
  TestServerConfigService,
  Device,
  DevicesService,
  YangService,
  NotificationService,
  TestMode,
  AGENT_MODEL,
  CommonRegEx,
  LicensesPageService
} from '@nrwl/common-lib';
import { Subject, Subscription } from 'rxjs';
import { TruespeedSetupService } from '../../truespeed-setup.service';
import {
  TruespeedConfigFieldMapping as ConfigFieldMapping,
  TX_IMAGE,
  TRANSMIT_INTERFACE_TYPE,
  TRANSMIT_INTERFACE_VALUE,
  DEFINED_ADDRESS_TYPES,
  definedAddressValue,
  SUBNET_MAP
} from '../models/model';
import { takeUntil } from 'rxjs/operators';
import { ScheduleLicense } from '@nrwl/licenses';

@Component({
  selector: 'nrwl-truespeed-setup-basic-tab',
  templateUrl: './truespeed-setup-basic-tab.component.html',
  styleUrls: ['./truespeed-setup-basic-tab.component.scss']
})
export class TruespeedSetupBasicTabComponent implements OnInit, OnDestroy {
  @ViewChild('cidrInput') el: ElementRef;
  @Input() tab: BaseSetupTab;
  @Input() setupCtrl: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  @Input() otherTabStateHistory: IComponentStateHistory;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreValues?: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreAgentBasedConfig?: Function;
  @Input() previousOutput: outputParams;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onOtherTabDataValueChange = new EventEmitter<any>();
  @ViewChild('currentForm', { static: true }) form: NgForm;
  readonly unsubscribe: Subject<void> = new Subject();
  isDisabledScheduler = false;
  scheduler: SchedulerFormat;
  scheduleInfoData: SchedulerFormat = {
    repeatInterval: 0,
    timeStamp: '',
    endTime: '',
    isValid: true
  };
  schedulerEndOptions = {
    enabled: false,
    optionsList: []
  };
  //eslint-disable-next-line
  selectedOriginator: any;
  selectedDestination: any;
  outputs: Array<string>;
  config: any;
  private tabsubscription: Subscription;
  private streamSubscription: Subscription;
  newTestMode = true;
  editMode = false;
  cloneMode = false;
  srcAgentType: string;
  destAgentType: string;
  customerName = '';
  technicianName = '';
  contact = '';
  email = '';
  phone = '';
  technicianId = '';
  comments = '';
  disableSteamInputs = false;
  expireTimeData: Array<{ name: string; value: string }> = [];
  expirationDefaultValue = null;
  defaultExpirationTime: number = null;
  defaultExecCount = null;
  expirationTimeIntervalList = [0, 1, 2, 6, 12, 24, 36, 168];
  executionCountsOverwrite = true;
  expirationTimeOverwrite = true;
  maxExecutionCountValue = 100;
  showExecutionCountField = false;
  showExpirationField = false;
  formField = {
    upstream: null,
    downstream: null,
    threshold: null,
    upstreamThreshold: null,
    downstreamThreshold: null,
    expirationTime: null,
    executionCounts: null,
    txInterface: null,
    definedAddress: null,
    srcMacAddress: null,
    srcEquipmentMac: null,
    srcNetworkMac: null,
    srcIpAddress: null,
    srcSubnet: null,
    srcGateway: null,
    srcVlanID: null,
    destTxInterface: null,
    destDefinedAddress: null,
    destIpAddress: null,
    destSubnet: null,
    destGateway: null,
    destMacAddress: null,
    destEquipmentMac: null,
    destNetworkMac: null,
    destVlanID: null
  };
  testName = 'truespeed';
  testMode: string;
  isAgentToJmep = false;
  minCir = 1;
  showOrginatorDetails = false;
  showDestinationDetails = false;
  txTypeImage = TX_IMAGE.EQUIPMENT;
  destTxTypeImage = TX_IMAGE.EQUIPMENT;
  txInterfaceTypes = TRANSMIT_INTERFACE_TYPE;
  configMapping = ConfigFieldMapping;
  agentModel = AGENT_MODEL;
  srcDefinedAddressTypes = [...DEFINED_ADDRESS_TYPES];
  destDefinedAddressTypes = [...DEFINED_ADDRESS_TYPES];
  macRegex = CommonRegEx.MAC;
  validIPRegex: RegExp = CommonRegEx.IP_V4_V6;
  subnetRegEx: RegExp = CommonRegEx.SUBNET_MASK_REGEX;
  addressTypeValue = definedAddressValue;
  ipFields = {
    srcAssignedAddress: null,
    srcTestIpAddress: null,
    srcSubnet: null,
    srcGateway: null,
    destAssignedAddress: null,
    destTestIpAddress: null,
    destSubnet: null,
    destGateway: null
  };
  truespeedScheduleLicense = ScheduleLicense.TRUESPEED;
  formTooltip = {
    maxExecutionCounts:
      'Specify the maximum execution count of this test. The range of count is between 0 and 100, or Unlimited. 0 means Unlimited. This range is configured at the server.'
  };
  noDecimalPattern = CommonRegEx.NO_DECIMAL_REGEX;
  isThresholdOverridable = false;

  defaultDynamicSaturation = false;
  showDynamicSaturation = false;
  controllerConfig = null;
  constructor(
    private readonly licenseService: LicensesPageService,
    public setupService: TruespeedSetupService,
    readonly entityCache: EntityCacheProvider,
    readonly transformService: TransformationService,
    readonly serverConfigService: TestServerConfigService,
    readonly deviceService: DevicesService,
    readonly yangService: YangService,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.initializeData();
    this.streamSubscription = this.setupService.streamDataChangeEvents.subscribe(data => {
      if (data) {
        if (data.key === ConfigFieldMapping.UPSTREAM_CIR) {
          this.formField.upstream = data.value;
        }
        if (data.key === ConfigFieldMapping.DOWNSTREAM_CIR) {
          this.formField.downstream = data.value;
        }
        this.onDataEmit(data.key, data.value, data.valid);
        this.calculateUpstreamDownstreamThreshods();
      }
    });
    this.setupService;
    this.licenseService.activeTestLicenseDetails.subscribe(licenseData => {
      if (licenseData?.length) {
        this.isDisabledScheduler = !licenseData.some(license =>
          this.truespeedScheduleLicense?.includes(license.featureName)
        );
      }
    });
    this.setupService.serverConfigDetails.subscribe(data => {
      if (data?.truespeedThroughputThreshold) {
        this.isThresholdOverridable = data?.truespeedThroughputThreshold?.IsUserOverridable;
        if (
          !isNil(data?.truespeedThroughputThreshold?.['throughput-threshold']) &&
          this.newTestMode
        ) {
          this.formField.threshold = data?.truespeedThroughputThreshold?.['throughput-threshold'];
          if (!isNil(this.formField.threshold)) {
            this.calculateUpstreamDownstreamThreshods();
          }
        } else {
          this.formField.threshold = this.restoreFieldValues(ConfigFieldMapping.UPSTREAM_THRESHOLD);
          this.calculateUpstreamDownstreamThreshods();
        }
      } else {
        this.formField.threshold = this.restoreFieldValues(ConfigFieldMapping.UPSTREAM_THRESHOLD);
        this.calculateUpstreamDownstreamThreshods();
      }
    });
  }

  initializeData(): void {
    this.restoreAgentBasedConfig(null, TestMode.CLIENT_TO_MA);
    this.restoreFormValues();
    this.newTestMode = this.setupService.mode === 'new';
    this.editMode = this.setupService.editMode;
    this.cloneMode = this.setupService.mode === 'clone';
    if (this.setupService.controllerConfig) {
      this.setupService.controllerConfig.pipe(takeUntil(this.unsubscribe)).subscribe(response => {
        if (response.status === 'success') {
          this.controllerConfig = response.data.test;
          this.restoreControllerConfig();
        }
        this.restoreFormValues();
      });
    } else {
      this.restoreFormValues();
    }
    if (!this.newTestMode) {
      let modelAgentType = this.srcAgentType;
      if (this.selectedOriginator?.getModel() === AGENT_MODEL.JMEP_10G) {
        modelAgentType = this.selectedOriginator?.getModel();
      } else if (this.selectedDestination?.getModel() === AGENT_MODEL.JMEP_10G) {
        modelAgentType = this.selectedDestination?.getModel();
      }
      this.restoreAgentBasedConfig(modelAgentType, this.testMode);
      this.restoreFormValues();
    }
  }

  restoreControllerConfig(): void {
    if (this.controllerConfig) {
      if (this.newTestMode) {
        this.defaultExecCount = this.controllerConfig.defaultExecCount;
        this.defaultExpirationTime = this.controllerConfig.defaultExpTimeInterval;
      }
      this.expirationTimeIntervalList = this.controllerConfig.expTimeList;
      this.maxExecutionCountValue =
        this.controllerConfig.limitedMaxExecCount || this.controllerConfig.maxExecCount;
      this.executionCountsOverwrite = this.controllerConfig.execCountOverwrite;
      this.expirationTimeOverwrite = this.controllerConfig.expirationTimeOverwrite;

      if (this.newTestMode) {
        //Updating Advanced tab values
        let saturationEnabled = false;
        if (typeof this.controllerConfig.testRunSaturation !== 'undefined') {
          const saturationOption = this.controllerConfig.testRunSaturation.toString();
          saturationEnabled =
            saturationOption.toLowerCase() == 'yes' || saturationOption.toLowerCase() == 'true';
          this.onAdvancedDataEmit(ConfigFieldMapping.SATURATION, saturationEnabled, true);
        }

        if (typeof this.controllerConfig.testDynamicSaturation !== 'undefined') {
          const dynamicSatOption = this.controllerConfig.testDynamicSaturation.toString();
          this.defaultDynamicSaturation =
            dynamicSatOption.toLowerCase() == 'yes' ||
            (dynamicSatOption.toLowerCase() == 'true' && saturationEnabled);
          this.showDynamicSaturation = !isNil(
            this.restoreAdvancedFieldValues(ConfigFieldMapping.ENABLE_DYNAMIC_SATURATION)
          );
          if (this.showDynamicSaturation) {
            this.onAdvancedDataEmit(
              ConfigFieldMapping.ENABLE_DYNAMIC_SATURATION,
              this.defaultDynamicSaturation,
              true
            );
          }
        }

        if (typeof this.controllerConfig.testNumberOfWalks !== 'undefined') {
          this.onAdvancedDataEmit(
            ConfigFieldMapping.NUMBER_OF_WALKS,
            this.controllerConfig.testNumberOfWalks,
            true
          );
        }
        if (typeof this.controllerConfig.testStartingPercentOfCir !== 'undefined') {
          this.onAdvancedDataEmit(
            ConfigFieldMapping.STARTING_PERCENT,
            this.controllerConfig.testStartingPercentOfCir,
            true
          );
        }
      }
    }
  }

  restoreFormValues(): void {
    this.tab.form = this.form;
    this.config = this.tab.config;
    this.outputs = this.config.def.outputs;
    this.testName = this.restoreFieldValues(ConfigFieldMapping.ALIAS);
    this.customerName = this.restoreFieldValues(ConfigFieldMapping.CUSTOMER);
    this.contact = this.restoreFieldValues(ConfigFieldMapping.CONTACT);
    this.email = this.restoreFieldValues(ConfigFieldMapping.EMAIL);
    this.phone = this.restoreFieldValues(ConfigFieldMapping.PHONE);
    this.comments = this.restoreFieldValues(ConfigFieldMapping.COMMENTS);
    this.technicianId = this.restoreFieldValues(ConfigFieldMapping.TECHINICIANID);
    this.technicianName = this.restoreFieldValues(ConfigFieldMapping.TECHINICIAN);
    this.testMode = this.restoreFieldValues(ConfigFieldMapping.TEST_MODE);
    this.isAgentToJmep = this.testMode === TestMode.MA_TO_JMEP;
    this.formField.upstream = this.restoreFieldValues(ConfigFieldMapping.UPSTREAM_CIR);
    this.formField.downstream = this.restoreFieldValues(ConfigFieldMapping.DOWNSTREAM_CIR);
    this.formField.expirationTime = this.restoreFieldValues(ConfigFieldMapping.EXPIRATION_DATE);
    const executionCount =
      this.defaultExecCount ?? this.restoreFieldValues(ConfigFieldMapping.MAX_EXECUTION_COUNTS);
    this.formField.executionCounts = executionCount > 0 ? executionCount : null;
    this.expirationDefaultValue =
      this.defaultExpirationTime ??
      this.restoreFieldValues(ConfigFieldMapping.EXPIRATION_DEFAULT_VALUE);

    this.showExecutionCountField = !isNil(
      this.restoreFieldValues(ConfigFieldMapping.MAX_EXECUTION_COUNTS)
    );
    this.showExpirationField = !isNil(
      this.restoreFieldValues(ConfigFieldMapping.EXPIRATION_DEFAULT_VALUE)
    );
    this.setupExpirationDateDisplay();
    this.restoreScheduleParams(ConfigFieldMapping.SCHEDULER);
    this.selectedOriginator = this.restoreFieldValues(ConfigFieldMapping.SELECTED_ORIGINATOR);
    this.selectedDestination = this.restoreFieldValues(ConfigFieldMapping.SELECTED_DESTINATION);
    this.srcAgentType = this.restoreFieldValues(ConfigFieldMapping.AGENT_TYPE);
    this.formField.txInterface = this.restoreFieldValues(ConfigFieldMapping.SRC_TRANSMIT_INTERFACE);
    this.formField.definedAddress = this.restoreFieldValues(ConfigFieldMapping.SRC_DEFINED_ADDRESS);
    this.formField.srcMacAddress = this.restoreFieldValues(ConfigFieldMapping.SRC_MAC_ADDRESS);
    this.formField.srcEquipmentMac = this.restoreFieldValues(ConfigFieldMapping.SRC_EQUIPMENT_MAC);
    this.formField.srcNetworkMac = this.restoreFieldValues(ConfigFieldMapping.SRC_NETWORK_MAC);

    this.destAgentType = this.restoreFieldValues(ConfigFieldMapping.DEST_AGENT_TYPE);
    this.formField.destTxInterface = this.restoreFieldValues(
      ConfigFieldMapping.DEST_TRANSMIT_INTERFACE
    );
    this.formField.destDefinedAddress = this.restoreFieldValues(
      ConfigFieldMapping.DEST_DEFINED_ADDRESS
    );
    this.formField.destMacAddress = this.restoreFieldValues(ConfigFieldMapping.DEST_MAC_ADDRESS);
    this.formField.destEquipmentMac = this.restoreFieldValues(
      ConfigFieldMapping.DEST_EQUIPMENT_MAC
    );
    this.formField.destNetworkMac = this.restoreFieldValues(ConfigFieldMapping.DEST_NETWORK_MAC);
    this.formField.destSubnet = this.restoreFieldValues(ConfigFieldMapping.DEST_SUBNET);
    this.formField.destGateway = this.restoreFieldValues(ConfigFieldMapping.DEST_GATEWAY);
    this.formField.destVlanID = this.restoreFieldValues(ConfigFieldMapping.DEST_VLAN_ID);

    this.ipFields.srcAssignedAddress = this.restoreFieldValues(
      ConfigFieldMapping.SRC_ASSIGNED_IP_ADDRESS
    );
    this.ipFields.srcTestIpAddress = this.restoreFieldValues(
      ConfigFieldMapping.SRC_TEST_IP_ADDRESS
    );
    this.formField.srcSubnet = this.restoreFieldValues(ConfigFieldMapping.SRC_SUBNET);
    this.formField.srcGateway = this.restoreFieldValues(ConfigFieldMapping.SRC_GATEWAY);
    this.formField.srcIpAddress = this.restoreFieldValues(ConfigFieldMapping.SRC_TEST_IP_ADDRESS);
    this.formField.destIpAddress = this.restoreFieldValues(ConfigFieldMapping.DEST_TEST_IP_ADDRESS);
    this.getDataBasedOnAgentConfig();

    if (
      this.showFormField(ConfigFieldMapping.SRC_DEFINED_ADDRESS) &&
      this.srcAgentType === AGENT_MODEL.JMEP_10G
    ) {
      this.onTxInterfaceChange();
      this.onSrcVlanIdChange();
      this.onDataEmit(
        ConfigFieldMapping.SRC_ASSIGNED_IP_ADDRESS,
        this.ipFields.srcAssignedAddress,
        true
      );
    }

    if (
      this.showFormField(ConfigFieldMapping.DEST_DEFINED_ADDRESS) &&
      this.destAgentType === AGENT_MODEL.JMEP_10G
    ) {
      this.onDestTxInterfaceChange();
      this.onDataEmit(ConfigFieldMapping.DEST_VLAN_ID, this.formField.destVlanID, true);
      this.onDataEmit(
        ConfigFieldMapping.DEST_ASSIGNED_IP_ADDRESS,
        this.ipFields.destAssignedAddress,
        true
      );
    }
  }

  // eslint-disable-next-line
  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  // eslint-disable-next-line
  restoreAdvancedFieldValues(field: string): any {
    const data = this.otherTabStateHistory('truespeed.advanced.html', field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  restoreScheduleParams(field: string): void {
    const data = this.stateHistory(this.componentId, field);
    if (data && data.value !== REMOVED) {
      this.populateScheduler(data.value);
    }
  }

  populateScheduler(schedulerInfo: SchedulerFormat): void {
    this.scheduleInfoData.repeatInterval = schedulerInfo.repeatInterval;
    this.scheduleInfoData.timeStamp = schedulerInfo.timeStamp;
    this.scheduleInfoData.isValid = true;
    this.emitScedulerParams();
  }

  //** get scheduler param values from scheduler component */
  fetchSchedulerVal(event: SchedulerFormat): void {
    this.scheduler = event;
    this.scheduleInfoData.repeatInterval = event.repeatInterval;
    this.scheduleInfoData.timeStamp = this.setupService.getScheduleTimeStamp(event.timeStamp);
    this.scheduleInfoData.isValid = true;
    this.emitScedulerParams();
  }

  emitScedulerParams(): void {
    this.onDataEmit('scheduler', this.scheduleInfoData, this.scheduleInfoData.isValid);
  }

  updateTestIdentity(event: {
    testName: string;
    customer: string;
    technician: string;
    contact: string;
    email: string;
    phone: string;
    comments: string;
    technicianId: string;
    isValidName: boolean;
  }): void {
    this.onDataEmit('alias', event.testName, event.isValidName);
    this.onDataEmit('customer', event.customer, true);
    this.onDataEmit('technician', event.technician, true);
    this.onDataEmit('contact', event.contact, true);
    this.onDataEmit('email', event.email, true);
    this.onDataEmit('phone', event.phone, true);
    this.onDataEmit('comments', event.comments, true);
    this.onDataEmit('technicianId', event.technicianId, true);
    const controls = this.form?.form?.controls;
    const allValid = controls ? Object.values(controls).every(control => !control.invalid) : true;
    const isFormValid = event.isValidName && allValid;
    this.form.form?.setErrors(isFormValid ? null : { invalid: true });
  }

  ngOnDestroy(): void {
    if (this.tabsubscription) {
      this.tabsubscription.unsubscribe();
    }
    this.unsubscribe.complete();
    this.unsubscribe.next();
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
    }
  }

  // eslint-disable-next-line
  onAgentChange(data: any): void {
    this.isAgentToJmep = data.testMode === TestMode.MA_TO_JMEP ? true : false;
    this.selectedOriginator = this.deviceService.findDeviceByID(data.originatorId);
    this.selectedDestination = this.deviceService.findDeviceByID(data.destinationId);
    if (this.selectedOriginator) {
      this.onDataEmit(
        ConfigFieldMapping.SELECTED_ORIGINATOR,
        this.selectedOriginator,
        data.isValid
      );
      this.srcAgentType = this.selectedOriginator.getModel();
      this.onDataEmit('model', this.srcAgentType, true);

      if (data['engine-a-info']) {
        data['engine-a-info']['engine-type'] =
          this.srcAgentType === AGENT_MODEL.JMEP_10G ? this.srcAgentType : 'vprobe';
      }
      this.onDataEmit(ConfigFieldMapping.ENGINE_A_INFO, data['engine-a-info'], data.isValid);
    } else if (data.testMode) {
      this.restoreAgentBasedConfig(this.srcAgentType, data.testMode);
      this.restoreFormValues();
    }
    this.onDataEmit(ConfigFieldMapping.TEST_MODE, data.testMode, true);

    if (this.selectedDestination) {
      this.onDataEmit(
        ConfigFieldMapping.SELECTED_DESTINATION,
        this.selectedDestination,
        data.isValid
      );
      this.destAgentType = this.selectedDestination.getModel();
      if (data['engine-b-info']) {
        data['engine-b-info']['engine-type'] =
          this.destAgentType === AGENT_MODEL.JMEP_10G ? this.destAgentType : 'vprobe';
      }
      if (this.isAgentToJmep) {
        this.onDataEmit(ConfigFieldMapping.DESTINATION_IP, data.destinationIP, data.isValid);
      }
      this.onDataEmit(ConfigFieldMapping.ENGINE_B_INFO, data['engine-b-info'], data.isValid);
    }
    const modelAgentType =
      this.selectedOriginator?.getModel() || this.selectedDestination?.getModel();
    this.onDataEmit(ConfigFieldMapping.AGENT_TYPE, modelAgentType, true);
    this.restoreAgentBasedConfig(modelAgentType, this.testMode);
    this.restoreFormValues();
    this.calculateUpstreamDownstreamThreshods();
    this.restoreControllerConfig();
  }

  getDataBasedOnAgentConfig(): void {
    if (this.selectedOriginator) {
      this.srcAgentType = this.selectedOriginator.getModel();
      this.setupService.setOriginatorAgent(this.selectedOriginator);
      const capabilityData = this.setupService.getAgentLookUpData(
        this.config.lookup,
        this.selectedOriginator
      );
      this.ipFields.srcAssignedAddress =
        capabilityData[ConfigFieldMapping.SRC_ASSIGNED_IP_ADDRESS] ??
        this.ipFields.srcAssignedAddress;
      this.ipFields.srcTestIpAddress =
        capabilityData[ConfigFieldMapping.SRC_TEST_IP_ADDRESS] ?? this.ipFields.srcTestIpAddress;
      this.ipFields.srcSubnet = capabilityData[ConfigFieldMapping.SRC_SUBNET]
        ? this.findSubnetMapValue(capabilityData[ConfigFieldMapping.SRC_SUBNET])
        : this.ipFields.srcSubnet;
      this.ipFields.srcGateway =
        capabilityData[ConfigFieldMapping.SRC_GATEWAY] ?? this.formField.srcGateway;
      this.formField.srcMacAddress =
        capabilityData[ConfigFieldMapping.SRC_MAC_ADDRESS] ?? this.formField.srcMacAddress;
      this.formField.srcNetworkMac = capabilityData[ConfigFieldMapping.SRC_NETWORK_MAC];
      this.formField.srcEquipmentMac = capabilityData[ConfigFieldMapping.SRC_EQUIPMENT_MAC];
      this.formField.srcVlanID =
        capabilityData[ConfigFieldMapping.SRC_VLAN_ID] !== '-1'
          ? capabilityData[ConfigFieldMapping.SRC_VLAN_ID]
          : '';

      this.setMaxStreamData(this.selectedOriginator, 'maxDownStream');
      this.setMaxStreamData(this.selectedOriginator, 'maxUpStream');
      if (this.srcAgentType === AGENT_MODEL.JMEP_10G) {
        this.updateDefinedAddressType();
        this.onTxInterfaceChange();
      }
    }
    if (this.selectedDestination) {
      this.destAgentType = this.selectedDestination.getModel();
      this.setupService.setDestinationAgent(this.selectedDestination);
      this.onDataEmit(ConfigFieldMapping.DEST_AGENT_TYPE, this.destAgentType, true);
      const capabilityData = this.setupService.getAgentLookUpData(
        this.config.lookup,
        this.selectedDestination
      );
      this.ipFields.destAssignedAddress =
        capabilityData[ConfigFieldMapping.DEST_ASSIGNED_IP_ADDRESS] ??
        this.ipFields.destAssignedAddress;
      this.ipFields.destTestIpAddress =
        capabilityData[ConfigFieldMapping.DEST_TEST_IP_ADDRESS] ?? this.ipFields.destTestIpAddress;
      this.ipFields.destSubnet = capabilityData[ConfigFieldMapping.DEST_SUBNET]
        ? this.findSubnetMapValue(capabilityData[ConfigFieldMapping.DEST_SUBNET])
        : this.formField.destSubnet;
      this.ipFields.destGateway =
        capabilityData[ConfigFieldMapping.DEST_GATEWAY] ?? this.formField.destGateway;
      this.formField.destVlanID =
        capabilityData[ConfigFieldMapping.DEST_VLAN_ID] !== '-1'
          ? capabilityData[ConfigFieldMapping.DEST_VLAN_ID]
          : '';
      this.formField.destMacAddress =
        capabilityData[ConfigFieldMapping.DEST_MAC_ADDRESS] ?? this.formField.destMacAddress;
      this.formField.destNetworkMac = capabilityData[ConfigFieldMapping.DEST_NETWORK_MAC];
      this.formField.destEquipmentMac = capabilityData[ConfigFieldMapping.DEST_EQUIPMENT_MAC];

      if (this.destAgentType === AGENT_MODEL.JMEP_10G) {
        this.updateDestDefinedAddressType();
        this.onDestTxInterfaceChange();
      }
    }
    const selectedDevice = this.selectedDestination || this.selectedOriginator;
    if (selectedDevice) {
      this.setTcpPorts(selectedDevice);
    }

    if (this.srcAgentType !== AGENT_MODEL.JMEP_10G) {
      const agentCapability = this.selectedOriginator?.deviceData?.capabilities?.phy;
      this.ipFields.srcAssignedAddress = this.selectedOriginator?.deviceData?.['mgmtIPaddress'];
      this.formField.srcIpAddress = agentCapability?.['default-test-ip'];
      this.formField.srcGateway = agentCapability?.['default-test-gateway'];
      this.formField.srcSubnet = agentCapability?.['default-test-mask'];
      this.onDataEmit(
        ConfigFieldMapping.SRC_ASSIGNED_IP_ADDRESS,
        this.ipFields.srcAssignedAddress,
        true
      );
    }

    if (this.destAgentType !== AGENT_MODEL.JMEP_10G) {
      const agentCapability = this.selectedDestination?.deviceData?.capabilities?.phy;
      this.ipFields.destAssignedAddress = this.selectedDestination?.deviceData?.['mgmtIPaddress'];
      this.formField.destIpAddress = agentCapability?.['default-test-ip'];
      this.formField.destGateway = agentCapability?.['default-test-gateway'];
      this.formField.destSubnet = agentCapability?.['default-test-mask'];
      this.onDataEmit(
        ConfigFieldMapping.DEST_ASSIGNED_IP_ADDRESS,
        this.ipFields.destAssignedAddress,
        true
      );
    }
    if (
      this.setupService.editConfigSrcAgentTypes.includes(this.srcAgentType) &&
      this.setupService.editConfigDestAgentTypes.includes(this.destAgentType)
    ) {
      this.showOrginatorDetails = true;
      this.showDestinationDetails = true;
    } else {
      this.showOrginatorDetails = false;
      this.showDestinationDetails = false;
    }
    this.onSourceIpAddressChange();
    this.onSourceGatewayChange();
    this.onSourceSubnetChange();
    this.onDestinationIpAddressChange();
    this.onDestinationGatewayChange();
    this.onDestinationSubnetChange();
  }

  setTcpPorts(selectedDevice: Device): void {
    if (selectedDevice) {
      const portsList = selectedDevice.deviceData.capabilities?.['phy-list']
        ? selectedDevice.deviceData.capabilities?.['truespeed-list'].find(
            item => item['phy-id'] === selectedDevice.deviceData?.phyId
          )['tcp-ports']
        : selectedDevice.deviceData?.capabilities?.truespeed?.['tcp-ports'];

      let tcpPorts: any = portsList?.toString().replace(/[-,]/g, ' ').split(' ');
      if (tcpPorts?.length > 0) {
        if (portsList.indexOf('-') !== -1) {
          tcpPorts = this.setupService.getRange(parseInt(tcpPorts[0]), parseInt(tcpPorts[1]));
        }
        this.setupService.setTCPPorts(tcpPorts);
      }
    } else {
      this.setupService.tcpPorts = [];
    }
  }

  setMaxStreamData(selectedDevice: Device, streamName: string): void {
    const maxStream = selectedDevice.deviceData.capabilities?.['phy-list']
      ? selectedDevice.deviceData.capabilities?.['truespeed-list'].find(
          item => item['phy-id'] === selectedDevice.deviceData?.phyId
        )['max-bandwidth']
      : selectedDevice.deviceData?.capabilities?.truespeed?.['max-bandwidth'];
    if (maxStream > 0) {
      this.setupService[streamName] = maxStream;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  onAdvancedDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onOtherTabDataValueChange.emit({
      componentId: 'truespeed.advanced.html',
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  @Debounce(500)
  downstreamUpdated(): void {
    this.formField.downstream = this.setupService.roundToOneDecimal(this.formField.downstream);
    const valid = this.validityStreamCheck(
      this.formField.downstream,
      this.setupService.maxDownStream
    );
    this.onDataEmit(ConfigFieldMapping.DOWNSTREAM_CIR, this.formField.downstream, valid);
    this.setupService.streamDataChanged({
      key: ConfigFieldMapping.DOWNSTREAM_CIR,
      value: this.formField.downstream,
      valid: valid
    });
    this.calculateUpstreamDownstreamThreshods();
  }

  @Debounce(500)
  upstreamUpdated(): void {
    this.formField.upstream = this.setupService.roundToOneDecimal(this.formField.upstream);
    const valid = this.validityStreamCheck(this.formField.upstream, this.setupService.maxUpStream);
    this.onDataEmit(ConfigFieldMapping.UPSTREAM_CIR, this.formField.upstream, valid);
    this.setupService.streamDataChanged({
      key: ConfigFieldMapping.UPSTREAM_CIR,
      value: this.formField.upstream,
      valid: valid
    });
    this.calculateUpstreamDownstreamThreshods();
  }

  thresholdUpdated(): void {
    this.calculateUpstreamDownstreamThreshods();
  }

  calculateUpstreamDownstreamThreshods(): void {
    const threshold = Number(this.formField.threshold);
    if (threshold > 0) {
      this.formField.upstreamThreshold = Math.round(
        (threshold / 100) * Number(this.formField.upstream)
      );
      this.formField.downstreamThreshold = Math.round(
        (threshold / 100) * Number(this.formField.downstream)
      );
    } else {
      this.formField.upstreamThreshold = 0;
      this.formField.downstreamThreshold = 0;
    }
    this.onDataEmit(ConfigFieldMapping.UPSTREAM_THRESHOLD, this.formField.threshold, true);
    this.onDataEmit(ConfigFieldMapping.DOWNSTREAM_THRESHOLD, this.formField.threshold, true);
  }

  // eslint-disable-next-line
  onExpireTimeChange($event: any): void {
    let expirationDate = '';
    if (this.formField.expirationTime !== 'unlimited') {
      expirationDate = this.calculateExpirationTime(parseInt(this.formField.expirationTime));
      this.expireTimeData[0] = { name: expirationDate, value: expirationDate };
      this.formField.expirationTime = expirationDate;
    } else {
      expirationDate = '3000-01-01 00:00';
    }
    this.onDataEmit(ConfigFieldMapping.EXPIRATION_DATE, expirationDate, true);
  }

  // eslint-disable-next-line
  onExecutionCountUpdated(): void {
    const maxExecutionCount = this.formField.executionCounts ?? 0;
    this.onDataEmit(
      ConfigFieldMapping.MAX_EXECUTION_COUNTS,
      maxExecutionCount,
      maxExecutionCount >= 0 && maxExecutionCount <= this.maxExecutionCountValue
    );
  }

  setupExpirationDateDisplay(): void {
    if (this.expirationDefaultValue) {
      this.expireTimeData = [];
      let defaultTime = this.formField.expirationTime;
      if (this.newTestMode) {
        defaultTime = this.calculateExpirationTime(this.expirationDefaultValue);
      }
      if (this.formField.expirationTime === '3000-01-01 00:00') {
        defaultTime = 'Unlimited';
      } else {
        this.expireTimeData.push({ name: defaultTime, value: defaultTime });
      }
      this.expireTimeData.push({ name: defaultTime, value: defaultTime });
      let flagUnlimitedOption = false;
      this.expirationTimeIntervalList.forEach(interval => {
        if (interval !== 0) {
          const displayInterval = this.getDisplayIntervalForSelect(interval);
          this.expireTimeData.push({ name: displayInterval, value: interval.toString() });
        } else {
          // 0 is in Expiration Date List,will process this "Unlimited" condition at last.
          flagUnlimitedOption = true;
        }
      });
      // If Unlimited is allowed, then put the option in the end of the drop down menu.
      if (flagUnlimitedOption) {
        const displayInterval = 'Unlimited';
        this.expireTimeData.push({ name: displayInterval, value: 'unlimited' });
      }
      this.formField.expirationTime = defaultTime;
      this.onDataEmit(ConfigFieldMapping.EXPIRATION_DATE, defaultTime, true);
    }
  }

  getDisplayIntervalForSelect(interval: number): string {
    let displayInterval = '+ ' + interval;
    if (interval == 1) displayInterval += ' hr';
    else if (interval > 23) {
      const days = Math.floor(interval / 24);
      const hours = interval % 24;
      if (days == 1) displayInterval = '+ ' + days + ' day';
      else displayInterval = '+ ' + days + ' days';
      if (hours == 1) displayInterval += ' and ' + hours + ' hr';
      else if (hours > 1) displayInterval += ' and ' + hours + ' hrs';
    } else displayInterval += ' hrs';
    return displayInterval;
  }

  calculateExpirationTime(timeInterval: number): string {
    const date = new Date(Date.now() + new Date().getTimezoneOffset() * 60000);
    const msec = timeInterval * 3600 * 1000;
    date.setTime(date.getTime() + msec);
    const dateString =
      date.getFullYear() +
      '-' +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + date.getDate()).slice(-2) +
      ' ' +
      ('0' + date.getHours()).slice(-2) +
      ':' +
      ('0' + date.getMinutes()).slice(-2);
    return dateString;
  }

  /* max bandwidth should be calculate based on agent response */
  validityStreamCheck(data: number, maxBandWidth: number): boolean {
    if (this.isAgentToJmep) {
      this.minCir = 20;
    } else {
      this.minCir = 1;
    }
    return data ? +data <= maxBandWidth && +data >= this.minCir : false;
  }

  copyvTAURL(): void {
    if (this.srcAgentType === AGENT_MODEL.VTA_TS) {
      this.notificationService.onSuccess('vTA Test link has been copied to clipboard');
    } else {
      this.notificationService.onSuccess('vSPA Test link has been copied to clipboard');
    }
  }

  copyDMZURL(): void {
    this.notificationService.onSuccess('DMZ Test link has been copied to clipboard');
  }

  showFormField(field: string): boolean {
    return this.outputs.includes(field);
  }

  onTxInterfaceChange(): void {
    this.txTypeImage =
      this.formField.txInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? TX_IMAGE.EQUIPMENT
        : TX_IMAGE.NETWORK;
    this.formField.srcMacAddress =
      this.formField.txInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? this.formField.srcEquipmentMac
        : this.formField.srcNetworkMac;
    this.onSourceMacAddressChange();
    this.onDataEmit(ConfigFieldMapping.SRC_TRANSMIT_INTERFACE, this.formField.txInterface, true);
  }

  onDestTxInterfaceChange(): void {
    this.destTxTypeImage =
      this.formField.destTxInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? TX_IMAGE.EQUIPMENT
        : TX_IMAGE.NETWORK;
    this.formField.destMacAddress =
      this.formField.destTxInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? this.formField.destEquipmentMac
        : this.formField.destNetworkMac;
    this.onSourceMacAddressChange();
    this.onDataEmit(
      ConfigFieldMapping.DEST_TRANSMIT_INTERFACE,
      this.formField.destTxInterface,
      true
    );
  }

  updateDefinedAddressType(): void {
    if (this.formField.srcIpAddress === this.ipFields.srcAssignedAddress) {
      this.formField.definedAddress = definedAddressValue.MANAGEMENT_IP;
    } else if (this.formField.srcIpAddress === this.ipFields.srcTestIpAddress) {
      this.formField.definedAddress = definedAddressValue.TEST_IP;
    } else if (!this.ipFields.srcTestIpAddress || this.ipFields.srcTestIpAddress === '0.0.0.0') {
      if (this.formField.definedAddress === definedAddressValue.TEST_IP) {
        this.formField.definedAddress = definedAddressValue.MANAGEMENT_IP;
      }
      this.srcDefinedAddressTypes[0].enabled = false;
    } else {
      this.formField.definedAddress = definedAddressValue.TEST_IP;
      this.srcDefinedAddressTypes[0].enabled = true;
    }
    this.onDefinedAddressChange();
  }

  updateDestDefinedAddressType(): void {
    if (this.formField.destIpAddress === this.ipFields.destAssignedAddress) {
      this.formField.destDefinedAddress = definedAddressValue.MANAGEMENT_IP;
    } else if (this.formField.destIpAddress === this.ipFields.destTestIpAddress) {
      this.formField.destDefinedAddress = definedAddressValue.TEST_IP;
    } else if (!this.ipFields.destTestIpAddress || this.ipFields.destTestIpAddress === '0.0.0.0') {
      if (this.formField.destDefinedAddress === definedAddressValue.TEST_IP) {
        this.formField.destDefinedAddress = definedAddressValue.MANAGEMENT_IP;
      }
      this.destDefinedAddressTypes[0].enabled = false;
    } else {
      this.formField.destDefinedAddress = definedAddressValue.TEST_IP;
      this.destDefinedAddressTypes[0].enabled = true;
    }
    this.onDestDefinedAddressChange();
  }

  onDefinedAddressChange(): void {
    if (this.formField.definedAddress === definedAddressValue.TEST_IP) {
      this.formField.srcIpAddress = this.ipFields.srcTestIpAddress;
      this.formField.srcSubnet = this.ipFields.srcSubnet;
      this.formField.srcGateway = this.ipFields.srcGateway;
    } else if (this.formField.definedAddress === definedAddressValue.MANAGEMENT_IP) {
      this.formField.srcIpAddress = this.ipFields.srcAssignedAddress;
    }
    this.onDataEmit(ConfigFieldMapping.SRC_DEFINED_ADDRESS, this.formField.definedAddress, true);
    this.onDataEmit(ConfigFieldMapping.SRC_GATEWAY, this.formField.srcGateway, true);
    this.onDataEmit(ConfigFieldMapping.SRC_SUBNET, this.formField.srcSubnet, true);
    this.onSourceIpAddressChange();
  }

  onDestDefinedAddressChange(): void {
    if (this.formField.destDefinedAddress === definedAddressValue.TEST_IP) {
      this.formField.destIpAddress = this.ipFields.destTestIpAddress;
      this.formField.destSubnet = this.ipFields.destSubnet;
      this.formField.destGateway = this.ipFields.destGateway;
    } else if (this.formField.destDefinedAddress === definedAddressValue.MANAGEMENT_IP) {
      this.formField.destIpAddress = this.ipFields.destAssignedAddress;
    }
    this.onDataEmit(
      ConfigFieldMapping.DEST_DEFINED_ADDRESS,
      this.formField.destDefinedAddress,
      true
    );
    this.onDataEmit(ConfigFieldMapping.DEST_GATEWAY, this.formField.destGateway, true);
    this.onDataEmit(ConfigFieldMapping.DEST_SUBNET, this.formField.destSubnet, true);
    this.onDestinationIpAddressChange();
  }

  getAgentURLLabel(): string {
    return this.srcAgentType === AGENT_MODEL.VTA_TS ? 'vTA-TS Test Link' : 'vSPA-SAT-TS Test Link';
  }

  @Debounce(500)
  onSourceIpAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_TEST_IP_ADDRESS, this.formField.srcIpAddress, true);
  }

  @Debounce(500)
  onSourceMacAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_MAC_ADDRESS, this.formField.srcMacAddress, true);
  }

  @Debounce(500)
  onSourceSubnetChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_SUBNET, this.formField.srcSubnet, true);
  }

  @Debounce(500)
  onSourceGatewayChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_GATEWAY, this.formField.srcGateway, true);
  }

  onSrcVlanIdChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_VLAN_ID, this.formField.srcVlanID, true);
  }

  onDestinationMacAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.DEST_MAC_ADDRESS, this.formField.destMacAddress, true);
  }

  findSubnetMapValue(subnet: string): string {
    subnet = subnet.toString();
    if (subnet && !subnet.includes('.')) {
      return SUBNET_MAP[subnet] ?? subnet;
    }
    return subnet;
  }

  @Debounce(500)
  onDestinationIpAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.DEST_TEST_IP_ADDRESS, this.formField.destIpAddress, true);
  }

  @Debounce(500)
  onDestinationSubnetChange(): void {
    this.onDataEmit(ConfigFieldMapping.DEST_SUBNET, this.formField.destSubnet, true);
  }

  @Debounce(500)
  onDestinationGatewayChange(): void {
    this.onDataEmit(ConfigFieldMapping.DEST_GATEWAY, this.formField.destGateway, true);
  }
}
